import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { createAuthClient } from 'better-auth/react';
import type {
  AppAuthUser,
  AuthAdapterModule,
  AuthProviderProps,
  UseAuthResult,
  UseUserResult,
} from './types';
import { safeResumeTarget } from './resume';
import {
  passwordMeetsCredentialPolicy,
  passwordRequirementLabel,
  passwordRequirementMessage,
} from './passwordPolicy';

/**
 * Better Auth adapter (the production adapter — see vite.config.ts).
 *
 * Session model (plan §3.2/§3.3):
 * - Cookie session is the source of truth (httpOnly, set by the API).
 * - The bearer plugin returns a session token in the `set-auth-token`
 *   response header on sign-in; we hold it IN MEMORY ONLY and return it from
 *   getToken() so the app's existing `Authorization: Bearer` fetches keep
 *   working. After a reload the token is gone — getToken() returns null and
 *   the API verifier falls back to the cookie, so nothing breaks.
 * - getToken/signOut identities are stable (module-level token, empty dep
 *   useCallback) — the staging request-storm lesson (6d0701c) by construction.
 */

// import.meta.env exists under Vite; unit tests load this module under plain
// Node (tsx), where it is undefined.
const googleSignInEnabled = import.meta.env?.VITE_GOOGLE_SIGNIN === 'true';

let bearerToken: string | null = null;

type AuthCardVariant = 'panel' | 'image';

const AuthCardVariantContext = React.createContext<AuthCardVariant>('panel');

export const authClient = createAuthClient({
  baseURL: typeof window !== 'undefined' ? window.location.origin : undefined,
  basePath: '/api/auth',
  fetchOptions: {
    onSuccess: (ctx) => {
      const token = ctx.response.headers.get('set-auth-token');
      if (token) bearerToken = token;
    },
  },
});

export function AppAuthProvider({ children }: AuthProviderProps) {
  return <>{children}</>;
}

function useSessionState() {
  const session = authClient.useSession();
  return {
    isLoaded: !session.isPending,
    user: session.data?.user ?? null,
  };
}

export function SignedIn({ children }: { children: React.ReactNode }) {
  const { isLoaded, user } = useSessionState();
  return isLoaded && user ? <>{children}</> : null;
}

export function SignedOut({ children }: { children: React.ReactNode }) {
  const { isLoaded, user } = useSessionState();
  return isLoaded && !user ? <>{children}</> : null;
}

export function RedirectToSignIn() {
  const location = useLocation();
  return <Navigate to={`/sign-in?redirect_url=${encodeURIComponent(location.pathname + location.search)}`} replace />;
}

export function useAuth(): UseAuthResult {
  const { isLoaded, user } = useSessionState();

  const getToken = React.useCallback(async () => bearerToken, []);

  const signOut = React.useCallback(async () => {
    bearerToken = null;
    await authClient.signOut();
    window.location.assign('/');
  }, []);

  return {
    isLoaded,
    isSignedIn: Boolean(user),
    userId: user?.id ?? null,
    getToken,
    signOut,
  };
}

export function useUser(): UseUserResult {
  const { isLoaded, user } = useSessionState();

  const mapped: AppAuthUser | null = user
    ? {
        id: user.id,
        fullName: user.name ?? null,
        firstName: user.name ? user.name.split(' ')[0] : null,
        username: null,
        emailAddresses: [{ emailAddress: user.email }],
        primaryEmailAddress: { emailAddress: user.email },
      }
    : null;

  return { isLoaded, isSignedIn: Boolean(user), user: mapped };
}

function deriveNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local.replace(/[._-]+/g, ' ').trim() || email;
}

function GoogleButton({ redirectTo }: { redirectTo: string }) {
  const variant = React.useContext(AuthCardVariantContext);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  if (!googleSignInEnabled) return null;
  const isImage = variant === 'image';
  const dividerClass = isImage ? 'auth-image-divider' : 'my-4 flex items-center gap-3 text-xs text-text-faint';
  const dividerLineClass = isImage ? 'auth-image-divider-line' : 'h-px flex-1 bg-line';
  const className = isImage
    ? 'auth-image-google-button'
    : 'flex w-full items-center justify-center gap-2 rounded border border-line bg-bg-secondary px-3 py-2 text-sm text-text hover:bg-panel-secondary';

  async function continueWithGoogle() {
    setBusy(true);
    setError(null);
    try {
      const result = await authClient.signIn.social({ provider: 'google', callbackURL: redirectTo });
      if (result.data?.url) {
        window.location.href = result.data.url;
        return;
      }
      if (result.error) {
        console.error('[auth] Google sign-in failed', result.error);
        setError(result.error.message || 'Could not start Google sign in.');
        return;
      }
      setError('Could not start Google sign in.');
    } catch (caught) {
      console.error('[auth] Google sign-in failed', caught);
      setError('Could not start Google sign in.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className={dividerClass} aria-hidden="true">
        <span className={dividerLineClass} />
        <span>or</span>
        <span className={dividerLineClass} />
      </div>
      <button
        type="button"
        className={className}
        disabled={busy}
        onClick={() => void continueWithGoogle()}
      >
        <GoogleMark />
        Continue with Google
      </button>
      {error ? <p className={isImage ? 'auth-image-error-text mt-3' : 'mt-3 text-sm text-warn'}>{error}</p> : null}
    </>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
        <path d="m2 2 20 20" />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

type SignInStartMode = 'sign-in' | 'sign-up';

export function resolveSignInRedirectTarget({
  forceRedirectUrl,
  fallbackRedirectUrl,
  locationSearch,
}: {
  forceRedirectUrl?: string;
  fallbackRedirectUrl?: string;
  locationSearch: string;
}): string {
  const rawResumeTarget =
    forceRedirectUrl
    ?? fallbackRedirectUrl
    ?? new URLSearchParams(locationSearch).get('redirect_url');

  return rawResumeTarget ? safeResumeTarget(rawResumeTarget, { fallback: '/dashboard' }) : '/';
}

export function shouldTreatSignUpAsSent(signUpError: { status?: number; message?: string | null } | null | undefined): boolean {
  if (!signUpError) return false;
  return signUpError.status === 422 || /exist/i.test(signUpError.message ?? '');
}

export function getSignUpVerificationCallbackTarget(redirectTo: string): string {
  if (redirectTo.startsWith('/sign-up/profile')) return redirectTo;
  if (!redirectTo || redirectTo === '/') return '/sign-up/profile';
  return `/sign-up/profile?redirect_url=${encodeURIComponent(redirectTo)}`;
}

export function SignIn({
  forceRedirectUrl,
  fallbackRedirectUrl,
  startMode = 'sign-in',
  requestKey = 0,
}: {
  forceRedirectUrl?: string;
  fallbackRedirectUrl?: string;
  startMode?: SignInStartMode;
  requestKey?: number;
} = {}) {
  const location = useLocation();
  const redirectTo = resolveSignInRedirectTarget({
    forceRedirectUrl,
    fallbackRedirectUrl,
    locationSearch: location.search,
  });
  const signUpVerificationRedirectTo = getSignUpVerificationCallbackTarget(redirectTo);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [mode, setMode] = React.useState<'sign-in' | 'sign-up' | 'sign-up-sent' | 'forgot' | 'forgot-sent' | 'unverified'>(startMode);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setError(null);
    setShowPassword(false);
    setMode(startMode);
  }, [requestKey, startMode]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: signInError } = await authClient.signIn.email({ email, password });
    setBusy(false);
    if (!signInError) {
      // Full-page navigation (like signOut): the new document authenticates
      // from the fresh session cookie, so protected resume targets never race
      // the in-memory session store, which updates asynchronously after
      // sign-in and would otherwise bounce the user back to /sign-in.
      window.location.replace(redirectTo);
      return;
    }
    if (signInError.status === 403) {
      // Email exists but is unverified — BA blocks sign-in until verification.
      setMode('unverified');
      return;
    }
    setError('Incorrect email or password.');
  }

  async function submitSignUp(event: React.FormEvent) {
    event.preventDefault();
    if (!passwordMeetsCredentialPolicy(password)) {
      setError(passwordRequirementMessage);
      return;
    }
    setBusy(true);
    setError(null);
    const { error: signUpError } = await authClient.signUp.email({
      email,
      password,
      name: deriveNameFromEmail(email),
      callbackURL: signUpVerificationRedirectTo,
    });
    setBusy(false);
    if (signUpError) {
      // Enumeration-safe: existing-account errors read the same as success.
      if (shouldTreatSignUpAsSent(signUpError)) {
        setMode('sign-up-sent');
        return;
      }
      setError(signUpError.message || 'Could not create the account.');
      return;
    }
    setMode('sign-up-sent');
  }

  async function sendReset(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });
    setBusy(false);
    // Enumeration-safe: identical confirmation whether or not the email exists.
    setMode('forgot-sent');
  }

  async function resendVerification() {
    setBusy(true);
    await authClient.sendVerificationEmail({ email, callbackURL: signUpVerificationRedirectTo });
    setBusy(false);
  }

  if (mode === 'sign-up-sent') {
    return (
      <AuthCard key={mode} title="Check your email" variant="image">
        <p className="auth-image-body-text">
          If {email} is new to LifeDrawing.live, we've sent a verification
          link. Click it to finish creating your account — you'll be signed in
          automatically.
        </p>
        <button type="button" className="auth-image-secondary-link mt-6" onClick={() => setMode('sign-in')}>
          ← Back to sign in
        </button>
      </AuthCard>
    );
  }

  if (mode === 'sign-up') {
    return (
      <AuthCard key={mode} title="Create your account" variant="image">
        <form onSubmit={submitSignUp} className="auth-image-form">
          <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
          <Field
            label={`Password (${passwordRequirementLabel})`}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            inputAction={(
              <button
                type="button"
                className="auth-image-input-action"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
              >
                <PasswordVisibilityIcon visible={showPassword} />
              </button>
            )}
          />
          {error && <p className="auth-image-error-text">{error}</p>}
          <button type="submit" className="auth-image-primary-button" disabled={busy || !email || !passwordMeetsCredentialPolicy(password)}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <GoogleButton redirectTo={signUpVerificationRedirectTo} />
        <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
          <button type="button" className="auth-image-secondary-link" onClick={() => setMode('sign-in')}>
            Sign in
          </button>
        </div>
      </AuthCard>
    );
  }

  if (mode === 'forgot' || mode === 'forgot-sent') {
    return (
      <AuthCard key={mode} title="Reset your password" variant="image">
        {mode === 'forgot-sent' ? (
          <p className="auth-image-body-text">
            If an account exists for {email || 'that address'}, we've emailed a reset link.
          </p>
        ) : (
          <form onSubmit={sendReset} className="auth-image-form">
            <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
            <button type="submit" className="auth-image-primary-button" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}
        <button type="button" className="auth-image-secondary-link mt-6" onClick={() => setMode('sign-in')}>
          ← Back to sign in
        </button>
      </AuthCard>
    );
  }

  if (mode === 'unverified') {
    return (
      <AuthCard key={mode} title="Verify your email" variant="image">
        <p className="auth-image-body-text">
          Your email address hasn't been verified yet. Check your inbox for the
          verification link, or resend it below.
        </p>
        <button type="button" className="auth-image-primary-button mt-6" disabled={busy} onClick={() => void resendVerification()}>
          {busy ? 'Sending…' : 'Resend verification email'}
        </button>
        <button type="button" className="auth-image-secondary-link mt-4" onClick={() => setMode('sign-in')}>
          ← Back to sign in
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard key={mode} title="Sign in to LifeDrawing.live" variant="image">
      <form onSubmit={submit} className="auth-image-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <Field
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          aside={(
            <button type="button" className="auth-image-field-aside" onClick={() => setMode('forgot')}>
              Forgot password?
            </button>
          )}
          inputAction={(
            <button
              type="button"
              className="auth-image-input-action"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((value) => !value)}
            >
              <PasswordVisibilityIcon visible={showPassword} />
            </button>
          )}
        />
        {error && <p className="auth-image-error-text">{error}</p>}
        <button type="submit" className="auth-image-primary-button" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <GoogleButton redirectTo={redirectTo} />
      <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
        <button type="button" className="auth-image-secondary-link" onClick={() => setMode('sign-up')}>
          Create an account
        </button>
      </div>
    </AuthCard>
  );
}

/** Reset-link landing form (route /reset-password?token=...). */
export function PasswordReset() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!passwordMeetsCredentialPolicy(password)) {
      setError(passwordRequirementMessage);
      return;
    }
    setBusy(true);
    setError(null);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    setBusy(false);
    if (resetError) {
      setError('This reset link is invalid or has expired. Request a new one from the sign-in page.');
      return;
    }
    navigate('/sign-in', { replace: true });
  }

  return (
    <AuthCard title="Set a new password">
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label={`New password (${passwordRequirementLabel})`} type="password" value={password} onChange={setPassword} autoComplete="new-password" />
        {error && <p className="text-sm text-warn">{error}</p>}
        <button type="submit" className="btn btn-accent w-full" disabled={busy || !passwordMeetsCredentialPolicy(password) || !token}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      </form>
    </AuthCard>
  );
}

export function UserButton({ children }: { children?: React.ReactNode; appearance?: unknown; afterSignOutUrl?: string }) {
  const { user } = useSessionState();
  const { signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const initial = (user?.name || user?.email || '?').trim()[0]?.toUpperCase() ?? '?';

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-label="Account menu"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-medium text-white"
      >
        {user?.image ? <img src={user.image} alt="" className="h-8 w-8 rounded-full object-cover" /> : initial}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-56 rounded-lg border border-line bg-panel py-2 shadow-lg">
          <div className="border-b border-line px-4 pb-2">
            <div className="truncate text-sm font-medium text-text">{user?.name}</div>
            <div className="truncate text-xs text-text-dim">{user?.email}</div>
          </div>
          <div onClick={() => setOpen(false)}>{children}</div>
          <button
            type="button"
            className="mt-1 flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-text hover:bg-bg-secondary"
            onClick={() => void signOut()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5M21 12H9" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

UserButton.MenuItems = function MenuItems({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
};

UserButton.Link = function Link({ href, label, labelIcon }: { href: string; label: string; labelIcon?: React.ReactNode }) {
  return (
    <a href={href} className="flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-bg-secondary">
      {labelIcon}
      {label}
    </a>
  );
};

function AuthCard({ title, children, variant = 'panel' }: {
  title: string;
  children: React.ReactNode;
  variant?: AuthCardVariant;
}) {
  const isImage = variant === 'image';
  return (
    <AuthCardVariantContext.Provider value={variant}>
      <div
        className={
          isImage
            ? 'auth-image-card'
            : 'mx-auto w-full max-w-md rounded-lg border border-line bg-panel p-6'
        }
      >
        <h1
          className={
            isImage
              ? 'auth-image-title'
              : 'font-display text-xl text-text'
          }
        >
          {title}
        </h1>
        {children}
      </div>
    </AuthCardVariantContext.Provider>
  );
}

function Field({ label, type, value, onChange, autoComplete, aside, inputAction }: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  aside?: React.ReactNode;
  inputAction?: React.ReactNode;
}) {
  const id = React.useId();
  const variant = React.useContext(AuthCardVariantContext);
  const isImage = variant === 'image';
  return (
    <div className="block text-sm">
      <div className={isImage ? 'auth-image-field-head' : 'flex items-baseline justify-between gap-3'}>
        <label
          className={isImage ? 'auth-image-field-label' : 'block text-text-dim'}
          htmlFor={id}
        >
          {label}
        </label>
        {aside}
      </div>
      <div className={isImage ? 'auth-image-control' : undefined}>
        <input
          id={id}
          type={type}
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
          className={
            isImage
              ? `auth-image-input${inputAction ? ' auth-image-input-with-action' : ''}`
              : 'mt-1 w-full rounded border border-line bg-bg px-3 py-2 text-sm text-text'
          }
        />
        {isImage ? inputAction : null}
      </div>
    </div>
  );
}

// Compile-time contract: this module must satisfy the adapter interface.
const adapterContract: AuthAdapterModule = {
  AppAuthProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  UserButton,
  SignIn,
  PasswordReset,
  useAuth,
  useUser,
};
void adapterContract;
